import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarPlus, Pencil, Trash2 } from 'lucide-react';
import { api } from '../api/client';
import type { ImportantDateWithOccurrence, Person } from '../api/types';
import { SectionHeading } from '../components/SectionHeading';
import { SkeletonCard } from '../components/SkeletonCard';
import { StatusBanner } from '../components/StatusBanner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { DateCard } from '../features/v1/DateCard';
import { formatDaysLeft, formatOccurrenceDate, formatPersonMeta } from '../utils/format';

export const PersonDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState<Person | null>(null);
  const [dates, setDates] = useState<ImportantDateWithOccurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const [personResult, datesResult] = await Promise.all([api.getPersonById(id), api.getPersonDates(id)]);
        setPerson(personResult);
        setDates(datesResult);
        setError(null);
      } catch (error) {
        setError(error instanceof Error ? error.message : '상세 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [id]);

  const handleDelete = async () => {
    if (!person) return;
    const confirmed = window.confirm(`${person.name} 및 연결된 중요 날짜를 모두 삭제할까요? 이 작업은 되돌릴 수 없습니다.`);
    if (!confirmed) return;
    setDeleting(true);
    try {
      await api.deletePerson(person.id);
      navigate('/people');
    } catch (error) {
      setError(error instanceof Error ? error.message : '삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <SkeletonCard />;

  if (error || !person) {
    return (
      <div className="space-y-5">
        <SectionHeading eyebrow="Detail" title="대상 상세" description="사람/반려동물 정보를 불러오지 못했습니다." />
        <StatusBanner title="상세 정보를 불러올 수 없습니다" tone="danger" description={error ?? '대상을 찾지 못했습니다.'} />
      </div>
    );
  }

  const nearestDate = dates[0];

  return (
    <div className="space-y-5">
      <SectionHeading
        eyebrow={formatPersonMeta(person)}
        title={person.name}
        description={person.memo || '메모가 없습니다.'}
        action={
          <div className="grid grid-cols-2 gap-2">
            <Button asChild variant="outline">
              <Link to="/people">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />목록
              </Link>
            </Button>
            <Button asChild>
              <Link to={`/people/${person.id}/dates/new`}>
                <CalendarPlus className="h-4 w-4" aria-hidden="true" />날짜 추가
              </Link>
            </Button>
          </div>
        }
      />

      {error ? <StatusBanner title="작업에 실패했습니다" tone="danger" description={error} /> : null}

      {nearestDate ? (
        <Card className="border-primary/30 bg-white">
          <CardContent className="p-5">
            <p className="text-sm font-semibold text-primary">가장 가까운 날짜</p>
            <div className="mt-2 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold tracking-[-0.025em]">{nearestDate.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {formatOccurrenceDate(nearestDate.occurrence.nextOccurrenceDate)}
                </p>
              </div>
              <Badge className="bg-primary text-primary-foreground">
                {formatDaysLeft(nearestDate.occurrence.daysLeft, nearestDate.occurrence.dDayLabel)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap gap-2">
            {person.tags.length > 0 ? (
              person.tags.map((tag) => (
                <Badge key={tag} className="bg-muted text-muted-foreground">
                  {tag}
                </Badge>
              ))
            ) : (
              <Badge className="bg-muted text-muted-foreground">태그 없음</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button asChild variant="outline">
              <Link to={`/people/${person.id}/edit`}>
                <Pencil className="h-4 w-4" aria-hidden="true" />대상 수정
              </Link>
            </Button>
            <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {deleting ? '삭제 중' : '대상 삭제'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-[-0.015em]">중요 날짜</h2>
        {dates.length > 0 ? (
          dates.map((date) => <DateCard key={date.id} date={date} showPerson={false} />)
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-5 text-sm font-semibold text-muted-foreground">아직 등록된 중요 날짜가 없습니다.</CardContent>
          </Card>
        )}
      </section>
    </div>
  );
};
